import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Trash2, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user === false) {
      navigate('/login');
      return;
    }
    fetchCart();
  }, [user]);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCartItems(data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Erreur lors du chargement du panier');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    try {
      await axios.put(`${API}/cart/${productId}?quantity=${newQuantity}`, {}, { withCredentials: true });
      fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${productId}`, { withCredentials: true });
      toast.success('Produit retiré du panier');
      fetchCart();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }
    if (cartItems.some((item) => item.product && item.quantity > item.product.stock)) {
      toast.error('Certains produits ne sont plus disponibles en quantité suffisante');
      return;
    }
    navigate('/checkout');
  };

  const total = cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-8" style={{ fontFamily: 'IBM Plex Sans' }}>
          Mon panier
        </h1>

        {cartItems.length === 0 ? (
          <div className="bg-white rounded-sm border border-border p-12 text-center">
            <p className="text-muted-foreground mb-4">Votre panier est vide</p>
            <Link
              to="/products"
              className="inline-block btn-primary px-6 py-3 rounded-sm font-semibold"
              data-testid="continue-shopping-link"
            >
              Continuer mes achats
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item.product_id}
                  className="bg-white rounded-sm border border-border p-6"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="flex gap-4">
                    <Link to={`/products/${item.product_id}`} className="flex-shrink-0">
                      <img
                        src={item.product?.images?.[0]}
                        alt={item.product?.name}
                        className="w-24 h-24 object-cover rounded-sm"
                      />
                    </Link>
                    <div className="flex-1">
                      <Link to={`/products/${item.product_id}`}>
                        <h3 className="font-semibold mb-2 hover:text-primary">
                          {item.product?.name}
                        </h3>
                      </Link>
                      <p className="text-xl font-bold text-primary mb-4">
                        {item.product?.price?.toFixed(2)} €
                      </p>
                      {item.product && item.quantity > item.product.stock && (
                        <p className="text-sm text-destructive font-medium mb-2" data-testid={`stock-warning-${item.product_id}`}>
                          {item.product.stock === 0
                            ? 'Produit en rupture de stock — retirez-le pour continuer'
                            : `Seulement ${item.product.stock} en stock — réduisez la quantité`}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border border-border rounded-sm">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            className="p-2 hover:bg-surface"
                            data-testid={`decrease-${item.product_id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-4 font-medium" data-testid={`quantity-${item.product_id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            disabled={item.product && item.quantity >= item.product.stock}
                            className="p-2 hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`increase-${item.product_id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item.product_id)}
                          className="text-destructive hover:text-destructive/80"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-sm border border-border p-6 sticky top-24">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'IBM Plex Sans' }}>
                  Résumé de la commande
                </h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sous-total</span>
                    <span className="font-medium" data-testid="subtotal">{total.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Livraison</span>
                    <span className="font-medium text-success">Gratuite</span>
                  </div>
                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between">
                      <span className="font-bold">Total</span>
                      <span className="font-bold text-xl text-primary" data-testid="total">
                        {total.toFixed(2)} €
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full btn-primary py-4 rounded-sm font-semibold"
                  data-testid="checkout-button"
                >
                  Passer la commande
                </button>
                <Link
                  to="/products"
                  className="block text-center mt-4 text-primary hover:underline"
                  data-testid="continue-shopping-bottom-link"
                >
                  Continuer mes achats
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;